import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '../../services/auth/auth.service';
import { NavigationService } from '../../services/navigation.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { BREAKPOINT_MEDIUM } from '../../shared/constants/breakpoints.constants';
import { TitleCasePipe } from '@angular/common';

/**
 * Header component that provides the application header with company branding and user information.
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    RouterLink,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TitleCasePipe,
  ]
})
export class HeaderComponent {
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #navigationService = inject(NavigationService);

  /** Current user's name */
  username = "";

  // Check if screen is expanded size (BREAKPOINT_MEDIUM: 840px and above)
  readonly isExpanded = toSignal(
    this.#breakpointObserver.observe(`(min-width: ${BREAKPOINT_MEDIUM}px)`)
  );

  readonly sidenavOpened = computed(() => {
    return this.#navigationService.mobileNavOpened();
  });

  /**
   * Toggle mobile sidenav open/closed state
   */
  toggleMobileNav(): void {
    this.#navigationService.toggleMobileNav();
  }

  /**
   * Handle user logout
   */
  logout(): void {
    this._authService.logout();
  }

  /**
   * Constructor for the HeaderComponent to read username from user's token
   */
  constructor(
    private _authService: AuthService,
  ) {
    // Subscribe to claim changes to update the username
    this._authService.claims$.subscribe((claims: Map<string, any> | null) => {
      this._updateUsername(claims);
    });
  }

  /**
   * Update the username from the claims (located as 'sub' in the JWT token)
   * 
   * @param claims - The claims object containing user information
   */
  private _updateUsername(claims: Map<string, any> | null): void {
    if (claims === null) return;

    if (claims.has('first_name') && claims.has('last_name')) {
      this.username = `${claims.get('first_name')} ${claims.get('last_name')}`;
    } else if (claims.has('sub')) {
      this.username = claims.get('sub') || '';
    } else {
      this.username = 'User'
    }
  }
}
